import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { IBigscreenSettings, IEventField } from "../../../../constants/interfaces";
import useEventFields from "../../../../hooks/useEventFields";

type Props = {
    initialValue: string | null,
    updateConfig: Function,
    isLoadingBigscreenSettings: boolean,
    bigscreenSettings?: IBigscreenSettings[],
    initialEventFields: IEventField[]
}

type Option = {
    value: string,
    label: string
}

const defaultMetricList = [
    { label: "Page views", value: "views" },
    { label: "Video views", value: "videoviews" },
    { label: "Conversions", value: "conversions" },
];

const usePrevious = <T extends unknown>(value: T): T | undefined => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

const ChartMetrics = ({ initialValue, updateConfig, bigscreenSettings, isLoadingBigscreenSettings, initialEventFields}: Props) => {
    const [value, setValue] = useState(initialValue !== 'events' ? initialValue : (initialEventFields.length ? initialEventFields[0].name : null));

    const eventFields = useEventFields(bigscreenSettings);
    const prevEventFields = usePrevious(eventFields);

    useEffect(() => {
        if (eventFields && eventFields.some(eventField => eventField.name === value)) {
            updateConfig(["graph_metric", "event_fields"], ["events", [eventFields.find(eventField => eventField.name === value)]]);
        } else {
            updateConfig(["graph_metric", "event_fields"], [value, []]);
        }
    }, [value, eventFields, updateConfig]);

    // Reset sort to default value if column is no longer available (e.g. brand is deselected)
    useEffect(() => {
        if (
            prevEventFields && eventFields
            && prevEventFields.find(eventField => eventField.name === value)
            && !eventFields.find(eventField => eventField.name === value)
        ) {
            setValue('views');
        }
    }, [prevEventFields, eventFields, value]);

    const getOptions = useCallback((): Option[] => {
        const metricList = [...defaultMetricList];

        if (eventFields !== undefined) {
            eventFields.forEach(eventField => {
                metricList.push({
                    value: eventField.name,
                    label: eventField.label
                });
            });
        }

        return metricList;
    }, [eventFields]);

    const type = useMemo(() => {
        if (!value) {
            return defaultMetricList[0];
        }

        return getOptions().find((metric: Option) => metric.value === value);
    }, [value, getOptions]);

    return (
        <div className="config-item__selects">
            <div className="form-group">
                <label htmlFor="chartMetric{index}">Chart metric:</label>
                <Select
                    id="chartMetric"
                    className="basic-single"
                    classNamePrefix="so-select-sort"
                    isSearchable={true}
                    isLoading={isLoadingBigscreenSettings}
                    value={type}
                    options={getOptions()}
                    onChange={(option: Option | null) => setValue(option!.value)}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: base => ({...base, zIndex: 9999})}}
                />
            </div>
        </div>
    );
};

ChartMetrics.defaultProps = {
    initialEventFields: [],
    isLoadingBigscreenSettings: {},
    initialValue: null
};

const mapStateToProps = (state: any) => {
    return {
        isLoadingBigscreenSettings: state.bigscreenSettings ? state.bigscreenSettings.isLoading : {}
    }
};

export default connect(mapStateToProps)(ChartMetrics);